import { useEffect, useRef, useState } from 'react';
import { applicationIdentityKey } from '../councils';
import { isSourceOnlyPlanningRecord, usesRetainedLondonDelivery } from './londonSharedDelivery';
import { fetchApplicationById } from '../services/applicationsApiService';

const hasResolvedInterestState = (application) => {
  if (!application || typeof application !== 'object') return false;
  const candidates = [
    application.interesting,
    application.worthWatching,
    application.worth_watching,
    application.isWorthWatching,
    application.is_worth_watching,
  ];

  return candidates.some((value) => {
    if (typeof value === 'boolean') return true;
    if (value === 0 || value === 1) return true;
    if (typeof value !== 'string') return false;
    const normalized = value.trim().toLowerCase();
    return ['true', 'false', '1', '0', 'yes', 'no'].includes(normalized);
  });
};

const describeDocuments = (documents) => {
  if (!Array.isArray(documents)) return null;
  return documents.map((document) => {
    if (!document || typeof document !== 'object') return String(document ?? '');
    return [
      document.id,
      document.url,
      document.document_url,
      document.title,
      document.name,
      document.updated_at,
    ];
  });
};

export const describePlanningApplicationDetailHydration = (application) => {
  const record = application && typeof application === 'object' ? application : {};
  const reference = String(record.reference_no || record.ref || '').trim();
  const council = String(record.source_council || '').trim();
  const identity = applicationIdentityKey(record);
  const hasCoreDetail = Boolean(
    record.description
    || record.ai_summary
    || record.status
    || Number.isFinite(Number(record.scale_rating))
    || record.link
  );
  const hasDocumentDetail = Array.isArray(record.documents);
  const hasInterestState = hasResolvedInterestState(record);
  // London lists omit documents; fetch its own detail once without waiting for
  // AI fields. An unchecked document list is a resolved detail response too.
  const sourceRecordOnly = isSourceOnlyPlanningRecord(record);
  const londonDetailLoaded = usesRetainedLondonDelivery(council) && record.london_detail_loaded === true;
  const londonRecord = usesRetainedLondonDelivery(council);
  const sourceVersion = String(
    record.updated_at
    || record.source_last_updated
    || record.scraped_date
    || record.scraped_at
    || record.lastmod
    || record.created_at
    || ''
  );
  const requestKey = JSON.stringify([
    identity,
    reference,
    council,
  ]);
  const sourceKey = JSON.stringify([
    requestKey,
    hasCoreDetail,
    hasDocumentDetail,
    hasInterestState,
    sourceRecordOnly,
    londonDetailLoaded,
    sourceVersion,
    record.description,
    record.ai_summary,
    record.status,
    record.scale_rating,
    record.link,
    record.address,
    record.interesting,
    record.worthWatching,
    record.worth_watching,
    record.isWorthWatching,
    record.is_worth_watching,
    describeDocuments(record.documents),
    record.document_index?.status,
    record.document_index?.last_checked_at,
    record.reported_source_facts?.source_record_sha256,
    record.reported_source_facts?.source_manifest_sha256,
    record.reported_source_facts?.source_captured_at,
    record.reported_source_facts?.decision,
    record.reported_source_facts?.decision_date,
    record.field_provenance,
  ]);

  return {
    requestKey,
    sourceKey,
    identity,
    reference,
    council,
    hasCoreDetail,
    hasDocumentDetail,
    hasInterestState,
    sourceRecordOnly,
    londonRecord,
    londonDetailLoaded,
  };
};

export function usePlanningApplicationDetailHydration({
  isOpen,
  application,
  onDetailHydrated,
}) {
  const [hydratedApplication, setHydratedApplication] = useState(null);
  const [isHydratingDetail, setIsHydratingDetail] = useState(false);
  const descriptor = describePlanningApplicationDetailHydration(application);
  const currentRef = useRef({ application, onDetailHydrated, descriptor });
  currentRef.current = { application, onDetailHydrated, descriptor };
  const hasCompleteDetail = descriptor.londonRecord ? descriptor.londonDetailLoaded : descriptor.sourceRecordOnly || (descriptor.hasCoreDetail
    && descriptor.hasDocumentDetail
    && descriptor.hasInterestState);

  useEffect(() => {
    const currentApplication = currentRef.current.application;

    if (!isOpen || !currentApplication || !descriptor.reference || hasCompleteDetail) {
      setIsHydratingDetail(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setIsHydratingDetail(true);

    fetchApplicationById(descriptor.reference, {
      council: descriptor.council || undefined,
      retries: 0,
      timeoutMs: 10000,
      fresh: !descriptor.hasInterestState,
      signal: controller.signal,
    })
      .then((detail) => {
        const current = currentRef.current;
        const latestApplication = current.application;
        if (
          cancelled
          || !detail
          || !latestApplication
          || current.descriptor.requestKey !== descriptor.requestKey
        ) return;
        const hydrated = {
          ...latestApplication,
          ...detail,
        };
        if (applicationIdentityKey(hydrated) !== descriptor.identity) return;
        setHydratedApplication({
          application: hydrated,
          requestKey: current.descriptor.requestKey,
          sourceKey: current.descriptor.sourceKey,
        });
        current.onDetailHydrated?.(hydrated);
      })
      .catch(() => {
        /* keep sparse application if detail hydration fails */
      })
      .finally(() => {
        if (!cancelled) setIsHydratingDetail(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [descriptor.requestKey, hasCompleteDetail, isOpen]);

  const visibleHydratedApplication = hydratedApplication
    && hydratedApplication.requestKey === descriptor.requestKey
    && hydratedApplication.sourceKey === descriptor.sourceKey
    ? hydratedApplication.application
    : null;

  return {
    hydratedApplication: visibleHydratedApplication,
    isHydratingDetail,
  };
}
